import type { StyleSpecification as MlStyleSpec } from "maplibre-gl";
import { latLngToCell, cellToLatLng } from "h3-js";
import { paperMapStyle } from "../../map/paper-style.js";
import { registerPmtilesProtocol } from "../../map/pmtiles-protocol.js";
import { DEFAULT_MAP_CENTER } from "../../map/tile-source.js";
import { radiusFeatureCollection, placesFeatureCollection, placeMarkersFC, placesBounds, coneFeatureCollection, spreadConesFC, spreadConesVectorFC, windConesVectorFC, cellsReachKm, type ConeInput, livePointFC, EMPTY_FC } from "../../map/home-radius.js";
import { HAZARD_KEYS, hazardIconMarkup } from "./hazard-icons.js";
import { TOKENS } from "../../design/tokens.js";
import { incidentMarkersFC, type IncidentView } from "./incident-model.js";
import { incidentFootprintFC } from "../../map/incident-overlay.js";
import { footprintFC } from "./footprint-layer.js";
import type { RawReportFC } from "./raw-report-model.js";

export interface MapHandle {
  /** Draw the watch ring + marker for every static monitored place (home + saved). */
  setPlaces(rings: Array<{ cell: string; radiusKm: number }>): void;
  /** Fit the viewport to every static watch zone + the live ring (on-demand). Disengages follow. */
  fitAll(rings: Array<{ cell: string; radiusKm: number }>, live: { lat: number; lng: number; radiusKm: number } | null): void;
  /** Replace incident markers. `nowMs` drives the E9 stale-fade / worsening prominence. */
  setIncidents(incidents: IncidentView[], nowMs: number): void;
  /** Update the spread cone overlay for a SINGLE incident (incident-detail mini-map). */
  setSpreadCone(pathCells: string[]): void;
  /** Draw spread cones for MANY incidents on the main map as analytic vector cones
   *  (arrow + fanned borders + speed ticks). `cells` still feeds the hidden debug overlay;
   *  the scalar fields drive the vector cone. Each cone carries its subject (tap → incident). */
  setSpreadCones(cones: Array<{ subject: string; cells: string[]; apex?: [number, number]; bearingDeg?: number; speedKmh?: number; confidence?: number; horizonMinutes?: number }>): void;
  /** Wind-prior forecast cones (one per incident): dashed/hollow vector + hidden debug cells. */
  setWindCone(cones: Array<{ cells: string[]; apex?: [number, number]; windBearingDeg?: number; windSpeedKmh?: number; speedKmh?: number; confidence?: number; horizonMinutes?: number }>): void;
  /** Show/hide the authoritative H3-cell debug overlay (You-tab advanced toggle). */
  setConeCellsVisible(on: boolean): void;
  /** Draw/clear the live-position marker + travel ring; null clears it. */
  setLivePosition(pos: { lat: number; lng: number; radiusKm: number } | null): void;
  /** Toggle camera-follow of the live marker; turning on recenters to the last live fix. */
  setFollow(on: boolean): void;
  /** Recenter the map on a res-8 cell (e.g. onboarding geolocation jump). No-op until loaded. */
  setCenter(cell: string): void;
  /** Replace the raw-report observation dots (power-user opt-in). Throttled to one
   *  update per animation frame. No-op until the load event fires. */
  setRawReports(fc: RawReportFC): void;
  /** Tear down the map (idempotent). */
  destroy(): void;
}

export interface MapOpts {
  centerCell?: string;
  /** Start framed on this [w, s, e, n] bbox (e.g. the served extract = a Europe overview)
   *  instead of a city zoom — used by the home picker until a location is known. */
  initialBounds?: [number, number, number, number];
  tilesUrl?: string;
  glyphsUrl?: string;
  /** Show a fixed pin at the map center; home = whatever is under it (precise placement). */
  centerPin?: boolean;
  /** Called with the res-8 cell under the center pin on every moveend (and once on load). */
  onCenterCell?: (cell: string) => void;
  /** Drop a static marker at `centerCell` (Now tab — shows "my home"). Drawn as a
   *  canvas circle layer (not a DOM Marker) so it needs no maplibre-gl.css and never
   *  drifts on zoom. */
  homeMarker?: boolean;
  /** Fit the viewport to the radius so all watch-area cells are visible (Now tab).
   *  Applied once per radius value; preserves the user's manual pan/zoom otherwise. */
  fitRadius?: boolean;
  /** Called when the map settles after panning/zooming, with the new center
   *  (lat, lng). Used to open firehose subscriptions for the viewed area. */
  onMoveEnd?: (lat: number, lng: number) => void;
  /** Called with an incident's subject DID when its marker is tapped — opens the
   *  incident detail overlay. */
  onIncidentClick?: (subject: string) => void;
  /** Called with the report id when a raw-report dot is tapped (power-user opt-in). */
  onRawReportClick?: (id: string) => void;
  /** Called with an incident's subject when its spread cone is tapped — opens its detail. */
  onSpreadClick?: (subject: string) => void;
  /** Fired when the user manually pans/zooms, so the app can drop "following" state. */
  onFollowDisengaged?: () => void;
}

const log = (...a: unknown[]): void => console.info("[papermap]", ...a);
const logErr = (...a: unknown[]): void => console.error("[papermap]", ...a);

function hasWebGL(): boolean {
  if (typeof WebGL2RenderingContext === "undefined" && typeof WebGLRenderingContext === "undefined") {
    log("hasWebGL: false (no WebGL globals — jsdom/SSR or WebGL disabled)");
    return false;
  }
  try {
    const c = document.createElement("canvas");
    const ok = !!c.getContext("webgl2") || !!c.getContext("webgl");
    if (!ok) log("hasWebGL: false (getContext returned null — GPU blocked/unavailable)");
    return ok;
  } catch (err) {
    logErr("hasWebGL: false (getContext threw)", err);
    return false;
  }
}

const INERT: MapHandle = {
  setPlaces() {}, fitAll() {}, setIncidents() {}, setSpreadCone() {}, setSpreadCones() {}, setWindCone() {}, setConeCellsVisible() {}, setLivePosition() {}, setFollow() {}, setCenter() {}, setRawReports() {}, destroy() {},
};

/** Mount the PaperMap into `container`. WebGL-guarded: without WebGL (tests,
 *  degraded clients) it renders the solid paper-cream placeholder and returns
 *  an inert handle. */
export function mountPaperMap(container: HTMLElement, opts: MapOpts = {}): MapHandle {
  log("mountPaperMap: called", {
    container: container?.id || container?.className || container?.tagName,
    opts: { centerCell: opts.centerCell, centerPin: opts.centerPin, homeMarker: opts.homeMarker },
  });
  if (!hasWebGL()) {
    log("mountPaperMap: WebGL unavailable → paper-cream placeholder, NO tiles will be requested");
    container.classList.add("paper-map--placeholder");
    return INERT;
  }

  // --- Closure state so setPlaces() calls made before the async import resolves
  //     are replayed once the style is loaded. ---
  let loaded = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- maplibre map ref, bridged across the dynamic import
  let mapRef: any = null;
  let ro: ResizeObserver | null = null;
  let pin: HTMLElement | null = null;
  let placesState: Array<{ cell: string; radiusKm: number }> = [];
  let incidentsState: IncidentView[] = [];
  let nowMsState = 0;
  let coneState: string[] = [];
  let windCellState: Array<{ cells: string[] }> = [];
  let fittedSig: string | null = null;
  let liveState: { lat: number; lng: number; radiusKm: number } | null = null;
  let followLive = true;
  let rawPending: RawReportFC | null = null;
  let rawRaf = 0;
  // When follow (re)engages, the NEXT live fix zooms the camera in to frame the travel
  // ring (close-up of "me"), instead of only recentering at the current zoom. Cleared
  // after that one fit so subsequent fixes just pan — the user can zoom freely and we
  // won't fight them. Re-armed on each setFollow(true) and on the first fix after a gap.
  let followZoomPending = true;
  const theme = TOKENS[matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"];
  const accent = theme.aid;
  const incidentAccent = theme.accent; // terracotta — incident footprint + markers (≠ teal home accent)
  const rawObs = "#5b6b7a"; // slate — raw observation dots (power-user opt-in, distinct from accent/incidentAccent)

  // Draw all static place rings + markers, and (once per distinct place-set) fit to them.
  const applyPlaces = (): void => {
    if (!loaded || !mapRef) return;
    mapRef.getSource("home-radius")?.setData(placesFeatureCollection(placesState));
    mapRef.getSource("place-markers")?.setData(placeMarkersFC(placesState));
    const sig = placesState.map((p) => `${p.cell}:${p.radiusKm}`).join("|");
    if (opts.fitRadius && sig && sig !== fittedSig) {
      fittedSig = sig;
      const b = placesBounds(placesState);
      if (b) mapRef.fitBounds([[b[0], b[1]], [b[2], b[3]]], { padding: 24, animate: false, maxZoom: 14 });
    }
  };

  const applyIncidents = (): void => {
    if (!loaded || !mapRef) return;
    mapRef.getSource("incidents")?.setData(incidentMarkersFC(incidentsState, nowMsState));
    mapRef.getSource("incident-footprint")?.setData(incidentFootprintFC(incidentsState));
    // Coarse area-warning footprint (v.footprint: res-5 covering cells, e.g. heat-wave districts).
    // De-duplicated union across all shown incidents that carry a footprint.
    const areaCells = [...new Set(incidentsState.flatMap((v) => v.footprint ?? []))];
    mapRef.getSource("area-footprint")?.setData(footprintFC(areaCells));
  };

  // Main map draws many incidents' cones (with subjects, for clicks); incident-detail draws
  // one. A given map uses one path or the other — prefer the multi-cone set when present.
  let coneSubjects: Array<{ subject: string; cells: string[] }> = []; // main-map debug cells
  let coneVectors: Array<ConeInput & { subject: string }> = [];       // main-map vector cones
  let windVectors: ConeInput[] = [];                                  // wind vector cones

  const applyCone = (): void => {
    if (!mapRef) return;
    const cellSrc = mapRef.getSource("spread-cone");
    if (cellSrc) cellSrc.setData(coneSubjects.length ? spreadConesFC(coneSubjects) : coneFeatureCollection(coneState));
    const vecSrc = mapRef.getSource("spread-cone-vec");
    if (vecSrc) vecSrc.setData(spreadConesVectorFC(coneVectors));
  };
  const applyWindCone = (): void => {
    if (!mapRef) return;
    const cellSrc = mapRef.getSource("wind-cone");
    if (cellSrc) cellSrc.setData(coneFeatureCollection(windCellState.flatMap((w) => w.cells)));
    const vecSrc = mapRef.getSource("wind-cone-vec");
    if (vecSrc) vecSrc.setData(windConesVectorFC(windVectors));
  };

  const applyLive = (): void => {
    if (!loaded || !mapRef) return;
    const ringSrc = mapRef.getSource("live-ring");
    const markSrc = mapRef.getSource("live-marker");
    if (!ringSrc || !markSrc) return;
    if (!liveState) {
      ringSrc.setData(EMPTY_FC);
      markSrc.setData({ type: "FeatureCollection", features: [] });
      return;
    }
    const { lat, lng } = liveState;
    ringSrc.setData(radiusFeatureCollection(lat, lng, liveState.radiusKm));
    markSrc.setData(livePointFC(lat, lng));
    if (!followLive) return;
    if (followZoomPending) {
      // Engaging follow zooms IN to the travel ring (the "follow-cells"), so toggling
      // follow ⇄ fit reads as "zoom to me" ⇄ "see all my places".
      followZoomPending = false;
      const b = placesBounds([], liveState);
      if (b) {
        mapRef.fitBounds([[b[0], b[1]], [b[2], b[3]]], { padding: 40, animate: true, maxZoom: 16 });
        return;
      }
    }
    mapRef.easeTo({ center: [lng, lat], duration: 400 });
  };

  // Center pin is a DOM overlay on the container (stays centered via CSS regardless
  // of map size); added immediately so it shows even before tiles load.
  if (opts.centerPin) {
    pin = document.createElement("div");
    pin.className = "paper-map__center-pin";
    pin.setAttribute("data-center-pin", "");
    container.append(pin);
  }

  void (async () => {
    try {
      log("init: importing maplibre-gl…");
      const maplibregl = (await import("maplibre-gl")).default;
      registerPmtilesProtocol(maplibregl as unknown as Parameters<typeof registerPmtilesProtocol>[0]);
      const mode = matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
      let center = DEFAULT_MAP_CENTER;
      let zoom = 6;
      if (opts.initialBounds) {
        const [w, s, e, n] = opts.initialBounds;
        center = [(w + e) / 2, (s + n) / 2];
        zoom = 3.5;
      } else if (opts.centerCell) {
        const [lat, lng] = cellToLatLng(opts.centerCell);
        center = [lng, lat];
        zoom = 11;
      }
      const tilesUrl = opts.tilesUrl ?? `pmtiles://${location.origin}/tiles/basemap.pmtiles`;
      const glyphsUrl = opts.glyphsUrl ?? `${location.origin}/tiles/fonts/{fontstack}/{range}.pbf`;
      const map = new maplibregl.Map({
        container,
        style: paperMapStyle({ mode, tilesUrl, glyphsUrl }) as unknown as MlStyleSpec,
        center,
        zoom,
      });
      mapRef = map;
      if (opts.initialBounds) {
        const b = opts.initialBounds;
        map.once("load", () => map.fitBounds([[b[0], b[1]], [b[2], b[3]]], { padding: 16, animate: false }));
      }
      // Region-neutral default view: with no home cell (browse-only), recenter on the
      // loaded basemap's OWN center from its PMTiles header — so the map opens on
      // whatever extract is served (Iberia, Europe, US, …), not a hardcoded region. The
      // initial `center` above is just the fallback while this resolves / if it fails.
      if (!opts.centerCell && !opts.initialBounds) {
        void (async () => {
          try {
            const { PMTiles } = await import("pmtiles");
            const h = await new PMTiles(tilesUrl.replace(/^pmtiles:\/\//, "")).getHeader();
            if (h && Number.isFinite(h.centerLon) && (h.centerLon !== 0 || h.centerLat !== 0)) {
              map.jumpTo({ center: [h.centerLon, h.centerLat], zoom: h.centerZoom || zoom });
            }
          } catch (err) {
            log("basemap header center unavailable — keeping fallback center", err);
          }
        })();
      }
      map.on("error", (e: { error?: unknown }) => logErr("map error", e?.error ?? e));
      // Only disengage follow when there is actually a live position being followed — a
      // normal pan of a map with no live fix must not spuriously fire onFollowDisengaged.
      map.on("dragstart", () => { if (followLive && liveState) { followLive = false; opts.onFollowDisengaged?.(); } });
      map.on("load", () => {
        log("map: 'load' fired (style ready)");
        // ⚠️ LAYER REQUIREMENT — text-font: any `type: "symbol"` layer with a `text-field` MUST
        // set `"text-font": ["Space Mono Regular"]` (the ONLY font the Beacon paper style ships
        // in its glyphs — see paper-style.ts). Omitting it defaults to a font that isn't in the
        // glyphs → the layer is an INVALID style → `addLayer` throws → it aborts THIS load handler,
        // so every layer/source/click-handler added after it silently never registers. (This bit
        // the raw-reports cluster-count layer: it killed the report dots.) Add glyph-dependent
        // symbol layers LAST so a font failure can't take out the circle/fill/line layers.
        map.addSource("home-radius", { type: "geojson", data: EMPTY_FC });
        map.addLayer({ id: "home-radius-fill", type: "fill", source: "home-radius",
          paint: { "fill-color": accent, "fill-opacity": 0.16 } });
        map.addLayer({ id: "home-radius-line", type: "line", source: "home-radius",
          paint: { "line-color": accent, "line-width": 1, "line-opacity": 0.5, "line-dasharray": [2, 2] } });
        // Place markers (home + saved) — populated via setPlaces(); teal canvas dots.
        map.addSource("place-markers", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
        map.addLayer({ id: "place-markers-dot", type: "circle", source: "place-markers",
          paint: { "circle-radius": 6, "circle-color": accent, "circle-stroke-width": 2, "circle-stroke-color": theme.paper } });
        // Area-warning footprint: coarse covering cells (res-5) from official area warnings
        // (e.g. heat waves, flood alerts). Faint terracotta wash under the fine cell layer.
        // Placed before incident-footprint so the coarser region stays visually behind
        // the per-cell cluster that crowd/satellite incidents produce.
        map.addSource("area-footprint", { type: "geojson", data: EMPTY_FC });
        map.addLayer({ id: "area-footprint-fill", type: "fill", source: "area-footprint",
          paint: {
            "fill-color": incidentAccent,
            "fill-opacity": 0.12,
            "fill-outline-color": incidentAccent,
          } });
        // Incident footprint: terracotta H3-cell fill marking each active incident's
        // affected cells (its extent, not just a center dot). Confirmed (W ≥ threshold)
        // reads solid; unconfirmed reads faint. Added after home-radius and before the
        // cone + markers so those stay legible on top.
        map.addSource("incident-footprint", { type: "geojson", data: EMPTY_FC });
        map.addLayer({ id: "incident-footprint-fill", type: "fill", source: "incident-footprint",
          paint: {
            "fill-color": incidentAccent,
            "fill-opacity": ["case", ["get", "confirmed"], 0.22, 0.1],
          } });
        map.addLayer({ id: "incident-footprint-line", type: "line", source: "incident-footprint",
          paint: {
            "line-color": incidentAccent,
            "line-width": ["case", ["get", "confirmed"], 1.5, 1],
            "line-opacity": ["case", ["get", "confirmed"], 0.6, 0.3],
          } });
        // Authoritative H3-cell debug overlay (hidden by default; You-tab advanced toggle).
        // These are the cells the alert engine actually keys on — surfaced for "why no alert?".
        map.addSource("spread-cone", { type: "geojson", data: EMPTY_FC });
        map.addLayer({ id: "spread-cone-fill", type: "fill", source: "spread-cone",
          layout: { visibility: "none" },
          paint: { "fill-color": theme.advisory, "fill-opacity": 0.18 } });
        map.addLayer({ id: "spread-cone-line", type: "line", source: "spread-cone",
          layout: { visibility: "none" },
          paint: { "line-color": theme.advisory, "line-width": 1, "line-opacity": 0.5 } });
        map.addSource("wind-cone", { type: "geojson", data: EMPTY_FC });
        map.addLayer({ id: "wind-cone-fill", type: "fill", source: "wind-cone",
          layout: { visibility: "none" },
          paint: { "fill-color": theme.advisory, "fill-opacity": 0.08 } });
        map.addLayer({ id: "wind-cone-line", type: "line", source: "wind-cone",
          layout: { visibility: "none" },
          paint: { "line-color": theme.advisory, "line-width": 1, "line-opacity": 0.4, "line-dasharray": [2, 2] } });

        // Vector cones (default view): direction arrow + fanned borders + speed ticks.
        // Wind-prior cone added first so the observed cone draws on top of it.
        map.addSource("wind-cone-vec", { type: "geojson", data: EMPTY_FC });
        map.addLayer({ id: "wind-cone-vec-fill", type: "fill", source: "wind-cone-vec",
          filter: ["==", ["get", "role"], "arrowhead"],
          paint: { "fill-color": theme.advisory, "fill-opacity": 0.04 } }); // hollow head
        map.addLayer({ id: "wind-cone-vec-line", type: "line", source: "wind-cone-vec",
          paint: { "line-color": theme.advisory, "line-width": 1, "line-opacity": 0.55, "line-dasharray": [2, 2] } });

        map.addSource("spread-cone-vec", { type: "geojson", data: EMPTY_FC });
        // Subtle wedge tint + filled arrowhead. Wedge fill stays the tap target (carries subject).
        map.addLayer({ id: "spread-cone-vec-fill", type: "fill", source: "spread-cone-vec",
          paint: { "fill-color": theme.advisory,
            "fill-opacity": ["match", ["get", "role"], "arrowhead", 0.9, "wedge", 0.08, 0] } });
        map.addLayer({ id: "spread-cone-vec-line", type: "line", source: "spread-cone-vec",
          paint: { "line-color": theme.advisory,
            "line-width": ["match", ["get", "role"], "tick", 1.5, "shaft", 2, 1],
            "line-opacity": 0.85 } });
        // Incident markers: terracotta circles geo-anchored on the map (distinct from
        // the teal home marker), one per nearby incident's representative cell.
        map.addSource("incidents", { type: "geojson", data: EMPTY_FC });
        map.addLayer({ id: "incidents-circle", type: "circle", source: "incidents",
          paint: {
            // E9: worsening incidents read larger; stale ones fade via per-feature opacity.
            "circle-radius": ["case", ["get", "worsening"], 9, 7],
            "circle-color": ["match", ["get", "status"], "contained", theme.contained, theme.accent],
            "circle-opacity": ["to-number", ["get", "opacity"]],
            "circle-stroke-width": 2,
            "circle-stroke-color": theme.paper,
          } });
        // Tap a marker → open its incident detail. The subject DID rides in the
        // feature properties (incidentMarkersFC); pointer affordance on hover.
        if (opts.onIncidentClick) {
          map.on("click", "incidents-circle", (e: { features?: { properties?: Record<string, unknown> }[] }) => {
            const subject = e.features?.[0]?.properties?.subject;
            if (typeof subject === "string" && subject) opts.onIncidentClick!(subject);
          });
          map.on("mouseenter", "incidents-circle", () => { map.getCanvas().style.cursor = "pointer"; });
          map.on("mouseleave", "incidents-circle", () => { map.getCanvas().style.cursor = ""; });
        }
        if (opts.onSpreadClick) {
          // A spread cone's wedge carries its incident subject → tap opens that incident.
          map.on("click", "spread-cone-vec-fill", (e: { features?: { properties?: Record<string, unknown> }[] }) => {
            const subject = e.features?.[0]?.properties?.subject;
            if (typeof subject === "string" && subject) opts.onSpreadClick!(subject);
          });
          map.on("mouseenter", "spread-cone-vec-fill", () => { map.getCanvas().style.cursor = "pointer"; });
          map.on("mouseleave", "spread-cone-vec-fill", () => { map.getCanvas().style.cursor = ""; });
        }
        // Raw reports (power-user opt-in): clustered observation dots. Distinct from incident
        // footprints — small semi-transparent dots; maplibre clusters them so a flooded area
        // stays bounded at any zoom. Empty until the RawReportLayer feeds setRawReports().
        map.addSource("raw-reports", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
          cluster: true,
          clusterRadius: 48,
          clusterMaxZoom: 14,
        });
        // Circle layers + click handlers FIRST so they are always added. The cluster-count
        // SYMBOL layer is added last (below): a symbol layer needs a glyph font, and if the
        // basemap has no glyphs maplibre rejects it — adding it last means that failure can
        // never abort the dots/cluster circles or their clicks.
        map.addLayer({
          id: "raw-reports-clusters", type: "circle", source: "raw-reports", filter: ["has", "point_count"],
          paint: {
            "circle-color": rawObs, "circle-opacity": 0.55,
            "circle-radius": ["step", ["get", "point_count"], 12, 25, 18, 100, 26],
          },
        });
        map.addLayer({
          id: "raw-reports-dots", type: "circle", source: "raw-reports", filter: ["!", ["has", "point_count"]],
          paint: { "circle-radius": 4, "circle-color": rawObs, "circle-opacity": 0.7, "circle-stroke-width": 1, "circle-stroke-color": theme.paper },
        });
        map.on("click", "raw-reports-dots", (e: { features?: { properties?: Record<string, unknown> }[] }) => {
          const id = e.features?.[0]?.properties?.id;
          if (typeof id === "string" && id) opts.onRawReportClick?.(id);
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- cluster feature geometry type conflicts with maplibre Position; access coords dynamically
        map.on("click", "raw-reports-clusters", (e: any) => {
          const c = e.features?.[0]?.geometry?.coordinates as [number, number] | undefined;
          if (c) map.easeTo({ center: c, zoom: Math.min((map.getZoom?.() ?? 10) + 2, 16) });
        });
        // Cluster count — uses the style's available font ("Space Mono Regular", same as the
        // place-label layer). Without an explicit, available text-font, maplibre defaults to
        // a font the Beacon paper style does not ship → invalid layer → broke the dots above.
        map.addLayer({
          id: "raw-reports-cluster-count", type: "symbol", source: "raw-reports", filter: ["has", "point_count"],
          layout: { "text-field": ["get", "point_count_abbreviated"], "text-font": ["Space Mono Regular"], "text-size": 11 },
          paint: { "text-color": theme.paper },
        });
        // Live position: travel ring (fill+line) + position marker (circle dot).
        // Added AFTER incident markers so live draws on top of everything.
        map.addSource("live-ring", { type: "geojson", data: EMPTY_FC });
        map.addLayer({ id: "live-ring-fill", type: "fill", source: "live-ring",
          paint: { "fill-color": theme.aid, "fill-opacity": 0.1 } });
        map.addLayer({ id: "live-ring-line", type: "line", source: "live-ring",
          paint: { "line-color": theme.aid, "line-width": 1.5, "line-opacity": 0.7 } });
        map.addSource("live-marker", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
        map.addLayer({ id: "live-marker-dot", type: "circle", source: "live-marker",
          paint: { "circle-radius": 7, "circle-color": theme.aid, "circle-stroke-width": 3, "circle-stroke-color": theme.paper } });
        // Category icons ON the markers (additive — the circle layers below stay as the hit
        // area + fallback). Register a light glyph per bucket as a map image, then symbol
        // layers keyed by the feature's `hazard`. Runs only on a real WebGL map.
        // Rasterise crisply: render the SVG at PR×base px and register with the matching
        // pixelRatio, so maplibre has enough source pixels on retina (40px @ pr2 was fuzzy).
        const PR = Math.min(Math.max(Math.round((typeof window !== "undefined" && window.devicePixelRatio) || 2), 2), 4);
        const SRC = 32 * PR; // 32 CSS px intrinsic, rendered at device resolution
        void (async () => {
          await Promise.all(HAZARD_KEYS.map((b) => new Promise<void>((resolve) => {
            const name = `hz-${b}`;
            if (map.hasImage(name)) { resolve(); return; }
            const img = new Image(SRC, SRC);
            img.onload = () => { try { if (!map.hasImage(name)) map.addImage(name, img, { pixelRatio: PR }); } catch { /* image race */ } resolve(); };
            img.onerror = () => resolve();
            img.src = "data:image/svg+xml;utf8," + encodeURIComponent(hazardIconMarkup(b, "#f5f1e6", SRC));
          })));
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- data-driven expr; maplibre's strict tuple type rejects the inferred array
          const iconImage: any = ["concat", "hz-", ["get", "hazard"]];
          if (!map.getLayer("incidents-icon")) {
            map.addLayer({ id: "incidents-icon", type: "symbol", source: "incidents",
              layout: { "icon-image": iconImage, "icon-size": 0.62, "icon-allow-overlap": true, "icon-ignore-placement": true },
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              paint: { "icon-opacity": ["coalesce", ["get", "opacity"], 1] as any } });
          }
          if (!map.getLayer("raw-reports-icon")) {
            map.addLayer({ id: "raw-reports-icon", type: "symbol", source: "raw-reports",
              filter: ["!", ["has", "point_count"]], layout: { "icon-image": iconImage, "icon-size": 0.58, "icon-allow-overlap": true, "icon-ignore-placement": true } });
          }
        })();
        loaded = true;
        applyPlaces();
        applyIncidents();
        applyCone();
        applyWindCone();
        applyLive();
        if (opts.centerPin && opts.onCenterCell) {
          const c = map.getCenter();
          opts.onCenterCell(latLngToCell(c.lat, c.lng, 8));
        }
      });
      if (opts.centerPin && opts.onCenterCell) {
        map.on("moveend", () => {
          const c = map.getCenter();
          opts.onCenterCell!(latLngToCell(c.lat, c.lng, 8));
        });
      }
      // Screens are built detached; container is often 0x0 at construction. Resize once sized.
      let sized = false;
      ro = new ResizeObserver(() => {
        if (container.clientWidth > 0 && container.clientHeight > 0) {
          if (!sized) { sized = true; log("map: container sized", `${container.clientWidth}x${container.clientHeight}`); }
          map.resize();
        }
      });
      ro.observe(container);
      if (opts.onMoveEnd) {
        map.on("moveend", () => {
          const c = map.getCenter();
          opts.onMoveEnd!(c.lat, c.lng);
        });
      }
    } catch (err) {
      logErr("init: map initialization threw — map will stay blank", err);
    }
  })();

  return {
    setPlaces(rings) {
      placesState = rings.filter((r) => r.cell);
      applyPlaces();
    },
    fitAll(rings, live) {
      followLive = false;
      opts.onFollowDisengaged?.();
      if (!loaded || !mapRef) return;
      const b = placesBounds(rings.filter((r) => r.cell), live ?? undefined);
      if (b) mapRef.fitBounds([[b[0], b[1]], [b[2], b[3]]], { padding: 24, animate: true, maxZoom: 14 });
    },
    setIncidents(incidents, nowMs) {
      incidentsState = incidents;
      nowMsState = nowMs;
      applyIncidents();
    },
    setSpreadCone(pathCells) {
      coneState = pathCells;
      coneSubjects = [];
      coneVectors = [];
      applyCone();
    },
    setSpreadCones(cones) {
      coneSubjects = cones.map((c) => ({ subject: c.subject, cells: c.cells }));
      coneVectors = cones
        .filter((c) => c.apex && typeof c.bearingDeg === "number" && typeof c.speedKmh === "number")
        .map((c) => ({ apex: c.apex!, bearingDeg: c.bearingDeg!, speedKmh: c.speedKmh!, confidence: c.confidence ?? 0, horizonMinutes: c.horizonMinutes ?? 0, subject: c.subject }));
      coneState = [];
      applyCone();
    },
    setWindCone(cones) {
      windCellState = cones.map((c) => ({ cells: c.cells }));
      // A stationary fire's estimate has NO fire speed (speedKmh absent) — the wind-prior
      // cone's reach lives in its windConeCells. Derive reach from those cells so the cone
      // still draws; it only needs an apex, a wind bearing, and a non-empty cell fan.
      windVectors = cones
        .filter((c) => c.apex && typeof c.windBearingDeg === "number" && c.cells.length > 0)
        .map((c) => ({
          apex: c.apex!,
          bearingDeg: c.windBearingDeg!,
          speedKmh: c.speedKmh ?? 0,
          confidence: c.confidence ?? 0,
          horizonMinutes: c.horizonMinutes ?? 0,
          reachKm: cellsReachKm(c.apex!, c.cells),
          // Real wind speed (when the estimate carries it) → literal time-to-reach ticks.
          tickSpeedKmh: c.windSpeedKmh,
        }));
      applyWindCone();
    },
    setConeCellsVisible(on) {
      if (!mapRef) return;
      const vis = on ? "visible" : "none";
      for (const id of ["spread-cone-fill", "spread-cone-line", "wind-cone-fill", "wind-cone-line"]) {
        if (mapRef.getLayer(id)) mapRef.setLayoutProperty(id, "visibility", vis);
      }
    },
    setRawReports(fc) {
      rawPending = fc;
      if (rawRaf) return;
      if (typeof requestAnimationFrame === "function") {
        rawRaf = requestAnimationFrame(() => {
          rawRaf = 0;
          const src = mapRef?.getSource("raw-reports") as { setData?: (d: unknown) => void } | undefined;
          if (src?.setData && rawPending) src.setData(rawPending);
          rawPending = null;
        });
      } else {
        // jsdom / SSR: no rAF, apply directly.
        const src = mapRef?.getSource("raw-reports") as { setData?: (d: unknown) => void } | undefined;
        if (src?.setData && rawPending) src.setData(rawPending);
        rawPending = null;
      }
    },
    setLivePosition(pos) {
      if (pos && !liveState) followZoomPending = true; // first fix after a gap → frame the ring
      liveState = pos;
      applyLive();
    },
    setFollow(on) {
      followLive = on;
      if (on) { followZoomPending = true; applyLive(); } // re-engage → zoom in to the travel ring
    },
    setCenter(cell) {
      if (!cell || !mapRef) return;
      const [lat, lng] = cellToLatLng(cell);
      // jumpTo (no animation) so the fixed center pin lands on the new cell and the
      // moveend handler re-fires onCenterCell with it. Zoom in to a neighborhood view.
      mapRef.jumpTo({ center: [lng, lat], zoom: Math.max(mapRef.getZoom?.() ?? 11, 12) });
    },
    destroy() {
      ro?.disconnect();
      pin?.remove();
      mapRef?.remove();
    },
  };
}
